import {
  Box,
  Button,
  Modal,
  SxProps,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ModalHeader, ModalBaseStyles } from "../Common/styles/modal";
import { camelCaseToWords } from "../../utils/audit";
import { StyledTableCell } from "../Common/styles/table";

type Props = {
  showModal: boolean;
  audit: any;
  closeModal: () => void;
};

const titleSx: SxProps = {
  fontWeight: "regular",
  lineHeight: "150%",
  color: "text.primary",
  mr: "10px",
  minWidth: "10ch",
};

const valueSx: SxProps = {
  lineHeight: "150%",
  color: "text.primary",
};

const containerSx: SxProps = {
  display: "flex",
  mb: "10px",
};

const AuditDetailsModal = ({ showModal, audit, closeModal }: Props) => {
  const handleCloseModal = () => {
    closeModal();
  };

  let details: any[] = [],
    valueDiff = [];

  if (audit.details) {
    const { diff, ...rest } = audit.details;

    Object.entries(rest).forEach(([k, v]) => {
      const val = typeof v === "boolean" ? (v ? "Yes" : "No") : v;
      details.push({
        key: camelCaseToWords(k),
        value: val,
      });
    });
    if (diff) {
      valueDiff = diff.map((row: any) => {
        let old =
          typeof row.old === "boolean" ? (row.old ? "Yes" : "No") : row.old;
        let newVal =
          typeof row.new === "boolean" ? (row.new ? "Yes" : "No") : row.new;
        if (typeof old !== "string" || typeof old !== "number") {
          old = old?.toString();
        }
        if (typeof newVal !== "string" || typeof newVal !== "number") {
          newVal = newVal?.toString();
        }

        return {
          key: camelCaseToWords(row.key),
          old: old,
          new: newVal,
        };
      });
    }
  }

  return (
    <Modal open={showModal} onClose={handleCloseModal}>
      <Box sx={{ ...ModalBaseStyles, minHeight: "10vh" }}>
        <ModalHeader title={audit.action} onCloseClick={handleCloseModal} />
        <Box sx={containerSx}>
          <Typography sx={titleSx} variant="body1">
            Date:
          </Typography>
          <Typography sx={valueSx} variant="body1">
            {audit.date}
          </Typography>
        </Box>
        <Box sx={containerSx}>
          <Typography sx={titleSx} variant="body1">
            Action:
          </Typography>
          <Typography sx={valueSx} variant="body1">
            {audit.action}
          </Typography>
        </Box>
        <Box sx={containerSx}>
          <Typography sx={titleSx} variant="body1">
            User:
          </Typography>
          <Typography sx={valueSx} variant="body1">
            {audit.user}
          </Typography>
        </Box>
        <Box sx={containerSx}>
          <Typography sx={titleSx} variant="body1">
            User Email:
          </Typography>
          <Typography sx={valueSx} variant="body1">
            {audit.userEmail}
          </Typography>
        </Box>
        {audit.participant && (
          <Box sx={containerSx}>
            <Typography sx={titleSx} variant="body1">
              Participant:
            </Typography>
            <Typography sx={valueSx} variant="body1">
              {audit.participant}
            </Typography>
          </Box>
        )}

        <>
          {details.map((row) => {
            return (
              <Box sx={containerSx} key={row.key}>
                <Typography sx={titleSx} variant="body1">
                  {row.key}:
                </Typography>
                <Typography sx={valueSx} variant="body1">
                  {row.value}
                </Typography>
              </Box>
            );
          })}
        </>

        {valueDiff.length > 0 ? (
          <Table sx={{ my: 2 }}>
            <TableHead>
              <TableRow>
                <StyledTableCell>Key</StyledTableCell>
                <StyledTableCell>Old value</StyledTableCell>
                <StyledTableCell>New Value</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {valueDiff.map((row: any) => {
                return (
                  <TableRow key={row?.key}>
                    <StyledTableCell sx={{ minWidth: "150px" }}>
                      <Typography fontSize="14px">{row?.key}</Typography>
                    </StyledTableCell>
                    <StyledTableCell sx={{ minWidth: "150px" }}>
                      <Typography fontSize="14px">{row?.old ?? "-"}</Typography>
                    </StyledTableCell>
                    <StyledTableCell sx={{ minWidth: "150px" }}>
                      <Typography fontSize="14px">{row?.new ?? "-"}</Typography>
                    </StyledTableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : null}

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button onClick={handleCloseModal} variant="outlined">
            Close
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AuditDetailsModal;
